import { useState } from 'react';
import type { AppV1Cfg, AppV1Row } from '@/lib/types/AppV1';
import {
  AppPreviewUtils,
  BILLBOARD_HEIGHT_FHD,
  HERO_GEOMETRY,
  HERO_LOGO_BOX_H,
  SCREEN_WIDTH_FHD,
  type HeroArt,
} from '../AppPreviewUtils';

const { fhd } = AppPreviewUtils;

interface AppTvBillboardProps {
  /** Only the theme is read — the series editor's hero preview passes a preset. */
  cfg: Pick<AppV1Cfg, 'theme'>;
  row: AppV1Row;
  /** The carousel position when the frame owns it (it decides the full-screen picture from the item); else internal. */
  index?: number;
  onIndexChange?: (index: number) => void;
  /**
   * What the band draws (design ADR-0016): the row's hero style on the plain
   * ground by default; `none` while a full-screen picture lies under the home
   * (the customer's Home image, or the one-item title page) — then the band is
   * TEXT ONLY: title, meta line, description, CTA, dots.
   */
  art?: HeroArt;
}

/**
 * Mirror of the shells' hero billboard (design: ott/design system/surfaces.md
 * "Hero billboard", ADR-0016 D3). Geometry is the shell's FHD layout expressed
 * in container-width units so the preview stays faithful at any frame size.
 * No blur anywhere: the ground is the plain colour and the art is the item's
 * RAW poster, always sharp.
 *
 * - card: the poster whole at 960x540, right edge x=1824, bottom y=648, crisp,
 *   square corners, no underlay, no feather.
 * - immersive: the poster at 1280x720 cropped to the band's 648 rows, anchored
 *   right; its left edge melts into the ground over 360 px and its bottom over
 *   the band's last 180 px.
 * - full_bleed: the poster at 1920x1080 cropped to the band; a left gradient
 *   to x=1100 and a bottom melt from y=400 — the text sits ON the picture.
 * The NavBar gradient over the band's top is the frame's (it also owns the
 * strip). Metadata block lower-left (title or ADR-0013 logo, the server's
 * `metaLine`, ≤3-line description, CTA that says what happens, page dots).
 * The carousel is static here: dots switch the item, hovering simulates focus.
 */
export function AppTvBillboard({ cfg, row, index, onIndexChange, art }: AppTvBillboardProps) {
  const { colors } = cfg.theme;
  const [ownIdx, setOwnIdx] = useState(0);
  const idx = index ?? ownIdx;
  const setIdx = onIndexChange ?? setOwnIdx;
  const [focused, setFocused] = useState(false);
  // ADR-0013 title slot: which logo URLs have loaded (and their native size), keyed
  // by URL so a rotation back to an item swaps at once and a failed load stays text.
  const [logosLoaded, setLogosLoaded] = useState<Record<string, { w: number; h: number }>>({});
  // Art that fails to load (an item with no artwork answers 404) is not drawn: the
  // title and the button stand on the plain ground, never a broken image.
  const [artFailed, setArtFailed] = useState<Record<string, true>>({});
  const failArt = (url: string) => setArtFailed((m) => (m[url] ? m : { ...m, [url]: true }));

  const items = row.items;
  const item = items[Math.min(idx, items.length - 1)];
  const drawn: HeroArt = art ?? AppPreviewUtils.heroStyle(row);
  const logoUrl = item.artwork.logo;
  const logoNatural = logoUrl ? logosLoaded[logoUrl] : undefined;
  const titleSlot = AppPreviewUtils.heroTitleSlot(item, logoNatural != null);
  const logoSize = logoNatural ? AppPreviewUtils.heroLogoSize(logoNatural.w, logoNatural.h) : null;
  const metaLine = item.metaLine?.trim();

  const posterSrc = AppPreviewUtils.rawPosterUrl(item);
  const posterUrl = posterSrc && !artFailed[posterSrc] ? posterSrc : null;

  const poster = (box: { left: number; top: number; w: number; h: number }, fit: 'cover' | 'contain', testId: string) =>
    posterUrl && (
      <img
        src={posterUrl}
        alt={item.title}
        data-testid={testId}
        onError={() => failArt(posterUrl)}
        style={{
          position: 'absolute',
          left: fhd(box.left),
          top: fhd(box.top),
          width: fhd(box.w),
          height: fhd(box.h),
          objectFit: fit,
          // a poster that is not 16:9 keeps the card's right and bottom edges where the contract puts them
          objectPosition: fit === 'contain' ? 'right bottom' : 'center',
          display: 'block',
        }}
      />
    );

  return (
    <div
      onMouseEnter={() => setFocused(true)}
      onMouseLeave={() => setFocused(false)}
      data-hero-art={drawn}
      style={{
        position: 'relative',
        width: '100%',
        height: fhd(BILLBOARD_HEIGHT_FHD),
        overflow: 'hidden',
        // the band has no ground of its own: the frame's plain colour, or the picture under it
        backgroundColor: 'transparent',
      }}
    >
      {/* card: the complete poster, crisp and square, nothing under or over it */}
      {drawn === 'card' &&
        (posterUrl ? (
          poster(HERO_GEOMETRY.card, 'contain', 'hero-art-card')
        ) : posterSrc ? null : (
          <div
            data-testid="hero-art-card"
            style={{
              position: 'absolute',
              left: fhd(HERO_GEOMETRY.card.left),
              top: fhd(HERO_GEOMETRY.card.top),
              width: fhd(HERO_GEOMETRY.card.w),
              height: fhd(HERO_GEOMETRY.card.h),
              backgroundColor: colors.bgSurface,
              color: colors.textSecondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: fhd(28),
            }}
          >
            {item.title}
          </div>
        ))}

      {/* immersive: the notch (1280x720 cropped to the band) anchored right, melting left and down */}
      {drawn === 'immersive' && posterUrl && (
        <>
          {poster(HERO_GEOMETRY.immersive, 'cover', 'hero-art-immersive')}
          <div
            data-testid="hero-melt-left"
            style={{
              position: 'absolute',
              left: fhd(HERO_GEOMETRY.immersive.left),
              top: 0,
              width: fhd(HERO_GEOMETRY.immersive.meltLeft),
              height: '100%',
              background: AppPreviewUtils.edgeMeltGradient(colors.bgPrimary, 'to right'),
            }}
          />
          <div
            data-testid="hero-melt-bottom"
            style={{
              position: 'absolute',
              left: fhd(HERO_GEOMETRY.immersive.left),
              top: fhd(BILLBOARD_HEIGHT_FHD - HERO_GEOMETRY.immersive.meltBottom),
              width: fhd(SCREEN_WIDTH_FHD - HERO_GEOMETRY.immersive.left),
              height: fhd(HERO_GEOMETRY.immersive.meltBottom),
              background: AppPreviewUtils.edgeMeltGradient(colors.bgPrimary, 'to top'),
            }}
          />
        </>
      )}

      {/* full_bleed: the poster across the whole width, cropped to the band, under the left gradient and the bottom melt */}
      {drawn === 'full_bleed' && posterUrl && (
        <>
          {poster(HERO_GEOMETRY.full_bleed, 'cover', 'hero-art-full-bleed')}
          <div
            data-testid="hero-melt-left"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: fhd(HERO_GEOMETRY.full_bleed.gradientWidth),
              height: '100%',
              background: AppPreviewUtils.edgeMeltGradient(colors.bgPrimary, 'to right'),
            }}
          />
          <div
            data-testid="hero-melt-bottom"
            style={{
              position: 'absolute',
              left: 0,
              top: fhd(HERO_GEOMETRY.full_bleed.meltTop),
              width: '100%',
              height: fhd(BILLBOARD_HEIGHT_FHD - HERO_GEOMETRY.full_bleed.meltTop),
              background: AppPreviewUtils.edgeMeltGradient(colors.bgPrimary, 'to top'),
            }}
          />
        </>
      )}

      {/* metadata block, lower-left inside the title-safe area */}
      <div
        style={{
          position: 'absolute',
          left: fhd(96),
          top: fhd(360),
          width: fhd(720),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        {/* title slot (ADR-0013): the logo replaces the typed title once loaded — a box
            at most 500x160 whose BOTTOM sits where the title's bottom sits, so it grows
            upward out of the 70px title line; text until then and on any failure */}
        {titleSlot === 'logo' && logoUrl && logoSize ? (
          <div
            style={{
              height: fhd(HERO_LOGO_BOX_H),
              marginTop: fhd(70 - HERO_LOGO_BOX_H),
              display: 'flex',
              alignItems: 'flex-end',
            }}
          >
            <img
              src={logoUrl}
              alt={item.title}
              style={{ width: fhd(logoSize.w), height: fhd(logoSize.h), display: 'block' }}
            />
          </div>
        ) : (
          <div
            style={{
              color: colors.textPrimary,
              fontSize: fhd(44),
              lineHeight: 1.2,
              fontWeight: 700,
              height: fhd(70),
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {item.title}
          </div>
        )}
        {/* the logo decodes off-screen; the swap happens on load, never on a guess */}
        {logoUrl && !logoNatural && (
          <img
            src={logoUrl}
            alt=""
            aria-hidden
            style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
            onLoad={(e) => {
              const el = e.currentTarget;
              if (el.naturalWidth > 0 && el.naturalHeight > 0) {
                setLogosLoaded((m) => (m[logoUrl] ? m : { ...m, [logoUrl]: { w: el.naturalWidth, h: el.naturalHeight } }));
              }
            }}
          />
        )}
        {/* meta line (contract Item.metaLine): smallest font, secondary at 0.75, 6px under the title */}
        {metaLine && (
          <div
            data-testid="hero-meta-line"
            style={{
              marginTop: fhd(6),
              color: colors.textSecondary,
              opacity: 0.75,
              fontSize: fhd(24),
              lineHeight: fhd(32),
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
            }}
          >
            {metaLine}
          </div>
        )}
        {item.descShort && (
          <div
            style={{
              marginTop: metaLine ? fhd(14) : fhd(12),
              width: fhd(680),
              color: colors.textSecondary,
              fontSize: fhd(30),
              lineHeight: fhd(40),
              display: '-webkit-box',
              WebkitLineClamp: AppPreviewUtils.heroDescriptionLines(titleSlot, Boolean(metaLine)),
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {item.descShort}
          </div>
        )}
        <div
          style={{
            marginTop: item.descShort ? fhd(20) : fhd(18),
            display: 'flex',
            alignItems: 'center',
            gap: fhd(24),
          }}
        >
          {/* CTA pill: accent while the billboard holds focus, quiet surface otherwise */}
          <div
            style={{
              width: fhd(176),
              height: fhd(56),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: focused ? colors.accent : colors.bgSurface,
              color: focused ? colors.textOnAccent : colors.textSecondary,
              fontSize: fhd(28),
              fontWeight: 600,
              transition: 'background-color 120ms ease, color 120ms ease',
            }}
          >
            {AppPreviewUtils.heroCtaLabel(item.kind)}
          </div>
          {items.length > 1 && (
            <div style={{ display: 'flex', gap: fhd(12) }}>
              {items.map((dotItem, i) => (
                <button
                  key={dotItem.idBC}
                  type="button"
                  aria-label={`Show spotlight item ${i + 1}`}
                  onClick={() => setIdx(i)}
                  style={{
                    width: fhd(14),
                    height: fhd(14),
                    borderRadius: '50%',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    backgroundColor: i === idx ? colors.accent : colors.textSecondary,
                    opacity: i === idx ? 1 : 0.45,
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
